import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import FloatingWhatsApp from './components/layout/FloatingWhatsApp';
import AdminLayout from './components/admin/AdminLayout';
import Home from './pages/Home';
import ShopList from './pages/ShopList';
import ShopDetail from './pages/ShopDetail';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminShops from './pages/admin/AdminShops';
import AdminApplications from './pages/admin/AdminApplications';
import AdminTenants from './pages/admin/AdminTenants';
import AdminSettings from './pages/admin/AdminSettings';
import AdminMessages from './pages/admin/AdminMessages';
import ClientLogin from './pages/client/ClientLogin';
import ClientDashboard from './pages/client/ClientDashboard';
import ClientApplicationDetail from './pages/client/ClientApplicationDetail';
import ClientMessages from './pages/client/ClientMessages';
import ClientImageUpload from './pages/client/ClientImageUpload';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ClientAuthProvider, useClientAuth } from './contexts/ClientAuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { useScrollToTop } from './hooks/useScrollToTop';
import Favicon from './components/Favicon';
import './App.css';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/admin/login" />;
}

function ClientProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useClientAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/client/login" />;
}

function PublicLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface">
      <Header />
      <main>{children}</main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

function ScrollToTopWrapper() {
  useScrollToTop();
  return null;
}

function AppContent() {
  return (
    <Router>
      <Favicon />
      <ScrollToTopWrapper />
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={
            <PublicLayout>
              <Home />
            </PublicLayout>
          } />
          <Route path="/shops" element={
            <PublicLayout>
              <ShopList />
            </PublicLayout>
          } />
          <Route path="/shops/:id" element={
            <PublicLayout>
              <ShopDetail />
            </PublicLayout>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={
            <div className="min-h-screen bg-gray-50">
              <AdminLogin />
            </div>
          } />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/shops" element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminShops />
              </AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/applications" element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminApplications />
              </AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/tenants" element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminTenants />
              </AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminSettings />
              </AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/messages" element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminMessages />
              </AdminLayout>
            </ProtectedRoute>
          } />
          
          {/* Client Portal Routes */}
          <Route path="/client/login" element={
            <div className="min-h-screen bg-gray-50">
              <ClientLogin />
            </div>
          } />
          <Route path="/client/dashboard" element={
            <ClientProtectedRoute>
              <ClientDashboard />
            </ClientProtectedRoute>
          } />
          <Route path="/client/applications/:id" element={
            <ClientProtectedRoute>
              <ClientApplicationDetail />
            </ClientProtectedRoute>
          } />
          <Route path="/client/applications/:id/messages" element={
            <ClientProtectedRoute>
              <ClientMessages />
            </ClientProtectedRoute>
          } />
          <Route path="/client/applications/:id/upload" element={
            <ClientProtectedRoute>
              <ClientImageUpload />
            </ClientProtectedRoute>
          } />
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ClientAuthProvider>
          <SocketProvider>
            <SettingsProvider>
              <AppContent />
            </SettingsProvider>
          </SocketProvider>
        </ClientAuthProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;